/**
 * NotificationsPage — W4.6 / F-H.2
 *
 * Route: /notifications (Auth)
 * Visuell nach notification-log.html Prototyp.
 *
 * F-H.2: Echte API-Anbindung via useNotifications (TanStack Query).
 * Filter: All / Unread / Alerts / Rewards
 * Mark-all-read: POST /api/v2/notifications/mark_all_read
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { notificationBadgeConfig } from './notificationBadge'
import { normalizeNotificationMessage } from './normalizeMessage'
import {
  useNotifications,
  useMarkAllRead,
  type Notification,
  type NotificationFilter,
} from './useNotifications'
import { toast } from '../../components/Toast/useToast'
import './notifications.css'

// Abnahme-Fix (P2): Prototyp zeigt absoluten, sortierbaren Zeitstempel
// ("2026-08-31 23:11") statt relativer Zeit — die relative Formatierung
// (samt DE/EN relativeTime-Keys) entfaellt hier ersatzlos.
function formatTimestamp(created_at: string): string {
  const d = new Date(created_at)
  if (Number.isNaN(d.getTime())) return created_at
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// --- Notification-Row ---

function NotificationRow({ notification: n }: { notification: Notification }) {
  const { t } = useTranslation('notifications')
  const cfg = notificationBadgeConfig(n.event_type)
  const Icon = cfg.icon
  const isUnread = n.read_at === null
  // Roh-Bodies aus FCM/Telegram enthalten \n und Einrückungen -> normalisieren
  const message = normalizeNotificationMessage(n.message)
  // ACCEPTANCE-Regression "Notification event TEXTS localized, not only chrome":
  // die Backend-Message/-Title kommen roh und unlokalisiert (kein
  // I18n.with_locale beim Versand, siehe UserMixin::Broadcast#send_fcm) —
  // die Zeilen-Überschrift wird daher komplett client-seitig aus dem
  // event_type übersetzt statt den rohen n.title zu übernehmen. Die
  // dynamischen Details (Hotspot-Name, Beträge) bleiben nur im Message-Body,
  // der naturgemäß nicht sicher nachübersetzt werden kann.
  const title = t(cfg.titleKey)

  return (
    <div
      className={`notif-row${isUnread ? ' unread' : ''}`}
      data-testid={`notif-row-${n.id}`}
    >
      {/* Icon-Chip — Farbe direkt gesetzt (nicht ueber identity-img/tone-N,
          siehe Kommentar in notificationBadge.ts) */}
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
          background: `color-mix(in oklch, ${cfg.accent} 14%, transparent)`,
          color: cfg.accent,
        }}
        aria-hidden="true"
      >
        <Icon size={16} />
      </div>

      {/* Inhalt */}
      <div>
        <div className="identity-title">
          {title}
          {isUnread && (
            <span
              className="dot"
              style={{
                background: 'var(--primary)',
                width: 6,
                height: 6,
                display: 'inline-block',
                marginLeft: 4,
                verticalAlign: 'middle',
                borderRadius: '50%',
              }}
              aria-label={t('status.unread')}
            />
          )}
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
          {message}
        </div>
      </div>

      {/* Channel-Spalte (ACCEPTANCE: channel column). Alle über
          GET /api/v2/notifications gelieferten Logs stammen aus
          UserMixin::Broadcast#send_fcm, also ausschließlich Push-Zustellung
          — es gibt kein `channel`-Feld im Contract, der einzige real
          vorkommende Wert ist deterministisch "push". Kind wird NICHT
          zusaetzlich als eigene Pill gezeigt (Abnahme-P2-Fix) — die
          Referenz zeigt genau eine Pill pro Zeile, Kind-Differenzierung
          laeuft ausschliesslich ueber Icon/Farbe (siehe cfg.accent oben). */}
      <div className="notif-col-md">
        <span className="pill" style={{ fontSize: 11 }}>{t('channel.push')}</span>
      </div>

      {/* Timestamp — absolut, nicht relativ (Abnahme-P2-Fix) */}
      <div className="muted mono notif-col-md" style={{ fontSize: 11 }}>
        {formatTimestamp(n.created_at)}
      </div>
    </div>
  )
}

// --- Empty State ---

function NotificationsEmpty({ isUnavailable }: { isUnavailable?: boolean }) {
  const { t } = useTranslation('notifications')

  if (isUnavailable) {
    return (
      <div
        data-testid="notifications-empty"
        style={{ textAlign: 'center', padding: '64px 16px', color: 'var(--text-subtle)' }}
      >
        <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>
          {t('empty.unavailable')}
        </p>
      </div>
    )
  }

  return (
    <div
      data-testid="notifications-empty"
      style={{ textAlign: 'center', padding: '64px 16px', color: 'var(--text-subtle)' }}
    >
      <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>{t('empty.title')}</p>
      <p style={{ fontSize: 13 }}>{t('empty.subtitle')}</p>
    </div>
  )
}

// --- Filter-Segment-Control ---

const FILTER_IDS: NotificationFilter[] = ['all', 'unread', 'alerts', 'rewards', 'bans', 'watched']

// --- Filter-Logik auf API-Daten ---

function filterApiNotifications(
  notifications: Notification[],
  filter: NotificationFilter,
): Notification[] {
  switch (filter) {
    case 'unread':
      return notifications.filter((n) => n.read_at === null)
    case 'alerts':
      return notifications.filter((n) => n.event_type === 'offline')
    case 'rewards':
      return notifications.filter((n) => n.event_type === 'reward')
    // ACCEPTANCE "kind filters incl. ban + watched": 'denylist' (Bann-Engine)
    // und 'listen' (Guard-IP/Adress-Änderung) sind die realen event_types
    // dafür, siehe notificationBadge.ts.
    case 'bans':
      return notifications.filter((n) => n.event_type === 'denylist')
    case 'watched':
      return notifications.filter((n) => n.event_type === 'listen')
    default:
      return notifications
  }
}

// --- Loading Skeleton ---

function NotificationsLoading() {
  const { t } = useTranslation('notifications')

  return (
    <div
      data-testid="notifications-loading"
      style={{ textAlign: 'center', padding: '64px 16px', color: 'var(--text-subtle)' }}
    >
      <p style={{ fontSize: 13 }}>{t('status.loading')}</p>
    </div>
  )
}

// --- Main Page ---

export function NotificationsPage() {
  const { t } = useTranslation('notifications')

  // FIN-SWEEP (shell-dashboard): document.title wird zentral in __root.tsx via
  // lib/seo.ts (ROUTE_KEY_MAP für /notifications) gesetzt und reagiert dort
  // bereits auf Sprachwechsel. Ein lokaler, hartkodiert-englischer Override
  // hier hätte diesen globalen, i18n-fähigen Mechanismus nach dem Mount
  // überschrieben (Bug: Titel blieb nach Sprachwechsel stehen).

  const [activeFilter, setActiveFilter] = useState<NotificationFilter>('all')

  const { data: response, isLoading, isError } = useNotifications(activeFilter)
  const markAllRead = useMarkAllRead()

  // null = 404 → Endpoint noch nicht verfügbar
  const isUnavailable = response === null || isError

  const rawList: Notification[] = response?.data ?? []
  const visible = filterApiNotifications(rawList, activeFilter)

  function handleMarkAllRead() {
    markAllRead.mutate(undefined, {
      onSuccess: () => {
        toast.success(t('toast.markAllReadSuccess'))
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : ''
        if (msg === '404') {
          toast.error(t('toast.markAllReadUnavailable'))
        } else {
          toast.error(t('toast.markAllReadError'))
        }
      },
    })
  }

  return (
    <div className="notifications-page" data-testid="notifications-page">
      {/* Issue #47: Alarm-Matrix ist nach der Migration nur teilweise wieder
          aktiv (Push ja, E-Mail/Telegram noch nicht) — ehrlicher Hinweis statt
          stillschweigend fehlender Kanäle. */}
      <div className="card card-pad" role="status" data-testid="notifications-rebuild-notice" style={{ marginBottom: 12 }}>
        {t('rebuildNotice')}
      </div>

      {/* Section Header */}
      <div className="section-header">
        <div>
          <h1>{t('page.title')}</h1>
          <p className="muted">
            {t('page.subtitle')}{' '}
            <span className="pill pill-success" style={{ fontSize: 10 }}>
              ● {t('page.live')}
            </span>
          </p>
        </div>
        <button
          className="btn btn-secondary"
          onClick={handleMarkAllRead}
          disabled={markAllRead.isPending}
          data-testid="mark-all-read"
        >
          {t('actions.markAllRead')}
        </button>
      </div>

      {/* Filter-Chips — eigene Zeile, getrennt vom Header (Abnahme-Fix P1) */}
      <div className="row notif-filter-chips" role="group" aria-label={t('filters.ariaLabel')}>
        {FILTER_IDS.map((id) => (
          <button
            key={id}
            className={`fchip${activeFilter === id ? ' on' : ''}`}
            onClick={() => setActiveFilter(id)}
            data-testid={`filter-${id}`}
          >
            {t(`filters.${id}`)}
          </button>
        ))}
      </div>

      {/* Liste — ein durchgehender Card-Container mit Trennlinien statt
          Einzel-Cards je Zeile (Abnahme-Fix P1, siehe notification-log.html) */}
      <div className="card" data-testid="notifications-list">
        {isLoading ? (
          <NotificationsLoading />
        ) : isUnavailable ? (
          <NotificationsEmpty isUnavailable />
        ) : visible.length === 0 ? (
          <NotificationsEmpty />
        ) : (
          visible.map((n) => <NotificationRow key={n.id} notification={n} />)
        )}
      </div>
    </div>
  )
}
